export const WFD_OBJECTIVES = [
  {
    id: 'alt_power',
    name: 'Alternative Power',
    description: 'Operate 100% on alternative power (generator, battery, solar, wind). All station equipment including laptops must run on alternative power. Lights and HVAC are exceptions.',
    multiplier: 1,
    autoDetectable: false
  },
  {
    id: 'away_from_home',
    name: 'Away From Home',
    description: 'Set up your field station more than ½ mile from your home.',
    multiplier: 3,
    autoDetectable: false
  },
  {
    id: 'multiple_antennas',
    name: 'Multiple Antennas',
    description: 'Deploy two or more antennas that have not been previously installed and make at least one contact on each.',
    multiplier: 1,
    autoDetectable: false
  },
  {
    id: 'sat_fm',
    name: 'FM Satellite Contact',
    description: 'Make at least 1 FM satellite contact during the operating period.',
    multiplier: 2,
    autoDetectable: false
  },
  {
    id: 'sat_ssb_cw',
    name: 'SSB/CW Satellite Contact',
    description: 'Make at least one contact using SSB or CW via satellite.',
    multiplier: 3,
    autoDetectable: false
  },
  {
    id: 'winlink',
    name: 'Winlink Email',
    description: 'Successfully send and receive at least one email from a winlink.org email address via amateur RF.',
    multiplier: 1,
    autoDetectable: false
  },
  {
    id: 'bulletin',
    name: 'WFD Special Bulletin',
    description: 'Accurately copy the WFD Special Bulletin message and submit with your log.',
    multiplier: 1,
    autoDetectable: false
  },
  {
    id: 'six_bands',
    name: '6 Bands',
    description: 'Make at least 3 contacts on at least 6 different bands.',
    multiplier: 6,
    autoDetectable: true,
    detectFn: 'checkSixBands'
  },
  {
    id: 'twelve_bands',
    name: '12 Bands',
    description: 'Make at least 3 contacts on at least 12 different bands.',
    multiplier: 6,
    autoDetectable: true,
    detectFn: 'checkTwelveBands'
  },
  {
    id: 'multiple_modes',
    name: 'Multiple Modes',
    description: 'Use multiple modes during the event (Phone and CW, CW and Digital, or Phone and Digital).',
    multiplier: 2,
    autoDetectable: true,
    detectFn: 'checkMultipleModes'
  },
  {
    id: 'qrp',
    name: 'QRP Operation',
    description: 'Operate using 10 watts or less on Phone, or 5 watts or less on CW/Digital for the entire event.',
    multiplier: 4,
    autoDetectable: false
  },
  {
    id: 'six_hours',
    name: '6 Continuous Hours',
    description: 'Operate for six continuous hours during the event.',
    multiplier: 2,
    autoDetectable: true,
    detectFn: 'checkSixHours'
  }
];

export const MODE_GROUPS = {
  phone: ['SSB', 'AM', 'FM', 'DMR', 'C4FM', 'DSTAR'],
  cw: ['CW'],
  digital: ['RTTY', 'PSK31', 'OLIVIA', 'PACKET', 'SSTV', 'ATV', 'JS8', 'FT8', 'FT4']
};

export const BANDS = [
  { label: '160m', min: 1.8, max: 2.0 },
  { label: '80m', min: 3.5, max: 4.0 },
  { label: '40m', min: 7.0, max: 7.3 },
  { label: '20m', min: 14.0, max: 14.35 },
  { label: '15m', min: 21.0, max: 21.45 },
  { label: '10m', min: 28.0, max: 29.7 },
  { label: '6m', min: 50.0, max: 54.0 },
  { label: '2m', min: 144.0, max: 148.0 },
  { label: '1.25m', min: 222.0, max: 225.0 },
  { label: '70cm', min: 420.0, max: 450.0 },
  { label: '33cm', min: 902.0, max: 928.0 },
  { label: '23cm', min: 1240.0, max: 1300.0 }
];

export function getBandFromFreq(freq) {
  const f = parseFloat(freq);
  for (const band of BANDS) {
    if (f >= band.min && f <= band.max) return band.label;
  }
  return 'Other';
}

export function getModeGroup(mode) {
  const m = mode.toUpperCase();
  if (MODE_GROUPS.cw.includes(m)) return 'cw';
  if (MODE_GROUPS.digital.includes(m)) return 'digital';
  return 'phone';
}

export function calculateObjectiveMultiplier(completedObjectives) {
  let total = 0;
  for (const obj of WFD_OBJECTIVES) {
    if (completedObjectives[obj.id]) {
      total += obj.multiplier;
    }
  }
  return total;
}

export function detectObjectives(contacts) {
  const detected = {};
  
  const bandCounts = {};
  const modeGroups = new Set();
  let minTime = null;
  let maxTime = null;
  
  contacts.forEach(c => {
    const band = getBandFromFreq(c.frequency);
    bandCounts[band] = (bandCounts[band] || 0) + 1;
    
    modeGroups.add(getModeGroup(c.mode));
    
    const time = new Date(c.timestamp).getTime();
    if (!minTime || time < minTime) minTime = time;
    if (!maxTime || time > maxTime) maxTime = time;
  });
  
  const bandsWithThree = Object.entries(bandCounts).filter(([_, count]) => count >= 3).length;
  detected.six_bands = bandsWithThree >= 6;
  detected.twelve_bands = bandsWithThree >= 12;
  
  detected.multiple_modes = modeGroups.size >= 2;
  
  if (minTime && maxTime) {
    const durationHours = (maxTime - minTime) / (1000 * 60 * 60);
    detected.six_hours = durationHours >= 6;
  }
  
  return detected;
}
